if (!customElements.get('bundle-widget')) {
  class BundleWidget extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.selections = {};
      this.discountApplied = false;
    }

    connectedCallback() {
      this.discountPct = parseInt(this.dataset.discount || '0', 10);
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.currentProductId = parseInt(this.dataset.currentProductId || '0', 10);
      this.preselected = this.dataset.preselected !== 'false';
      this.heading = this.dataset.heading || 'Bundle & Save';
      this.productHandles = (this.dataset.productHandles || '').trim();
      this.maxQty = Math.min(999, Math.max(1, parseInt(this.dataset.maxQuantity || '99', 10) || 99));
      this.minQty = Math.max(1, parseInt(this.dataset.minQuantity || '3', 10) || 3);
      this.moneyFormat = window.FoxTheme && FoxTheme.settings ? FoxTheme.settings.moneyFormat : '${{amount}}';

      if (!this.productHandles) {
        this.innerHTML = '';
        this.setAttribute('hidden', '');
        return;
      }

      this.fetchBundleProducts();
    }

    parseHandlesAndQuantities() {
      const entries = this.productHandles.split(',').filter((s) => s.trim());
      return entries.map((entry) => {
        const parts = entry.trim().split(':');
        const handle = parts[0].trim();
        const qty = parts.length > 1 ? parseInt(parts[1], 10) : 1;
        return { handle, qty: this.clampQty(qty || 1) };
      });
    }

    async fetchBundleProducts() {
      try {
        const parsed = this.parseHandlesAndQuantities();
        const results = await Promise.all(
          parsed.map((p) => this.fetchProductByHandle(p.handle))
        );

        this.products = [];
        results.forEach((prod, i) => {
          if (prod) {
            prod._defaultQty = parsed[i].qty;
            this.products.push(prod);
          }
        });

        if (this.products.length === 0) {
          this.innerHTML = '';
          this.setAttribute('hidden', '');
          return;
        }

        let availableDefaultQty = 0;
        this.products.forEach((prod) => {
          const firstAvailable = prod.variants.find((v) => v.available) || prod.variants[0];
          const hasAvailable = prod.variants.some((v) => v.available);
          this.selections[prod.id] = {
            checked: this.preselected,
            variantId: firstAvailable.id,
            variant: firstAvailable,
            quantity: prod._defaultQty || 1,
          };
          if (hasAvailable) {
            availableDefaultQty += prod._defaultQty || 1;
          }
        });

        if (availableDefaultQty < this.minQty) {
          this.innerHTML = '';
          this.setAttribute('hidden', '');
          return;
        }

        this.render();
      } catch (err) {
        console.error('BundleWidget: error', err);
        this.innerHTML = '';
        this.setAttribute('hidden', '');
      }
    }

    async fetchProductByHandle(handle) {
      try {
        const r = await fetch(`/products/${handle.trim()}.js`);
        if (!r.ok) return null;
        return await r.json();
      } catch {
        return null;
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.round(cents / 100).toString();
      const amountWithComma = amount.replace('.', ',');

      let formatted = this.moneyFormat;
      formatted = formatted.replace('{{amount_with_comma_separator}}', amountWithComma);
      formatted = formatted.replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
      formatted = formatted.replace('{{amount_no_decimals}}', amountNoDecimals);
      formatted = formatted.replace('{{amount}}', amount);
      return formatted;
    }

    getDiscountedPrice(price) {
      if (this.discountPct <= 0) return price;
      return price - Math.floor(price * this.discountPct / 100);
    }

    clampQty(n) {
      const v = parseInt(n, 10);
      if (Number.isNaN(v) || v < 1) return 1;
      return Math.min(this.maxQty, v);
    }

    getPricesHtmlForProduct(prod, meetsMinimum) {
      const sel = this.selections[prod.id];
      const variant = sel.variant;
      const qty = this.clampQty(sel.quantity || 1);
      sel.quantity = qty;

      if (!variant.available) {
        return `<span class="bw-product__sold-out">Sold out</span>`;
      }

      const unit = variant.price;
      const lineOrig = unit * qty;

      if (this.discountPct > 0 && meetsMinimum !== false) {
        const unitSale = this.getDiscountedPrice(unit);
        const lineSale = unitSale * qty;
        let h = `<span class="bw-product__price-sale">${this.formatMoney(lineSale)}</span>`;
        h += `<span class="bw-product__price-original">${this.formatMoney(lineOrig)}</span>`;
        return h;
      }

      return `<span class="bw-product__price-regular">${this.formatMoney(lineOrig)}</span>`;
    }

    getSelectedItems() {
      return this.products.filter(
        (p) => this.selections[p.id] && this.selections[p.id].checked && this.selections[p.id].variant.available
      );
    }

    calculateTotals() {
      const selectedItems = this.getSelectedItems();
      let originalTotal = 0;
      let discountedTotal = 0;
      let totalQty = 0;

      selectedItems.forEach((prod) => {
        const sel = this.selections[prod.id];
        const qty = this.clampQty(sel.quantity || 1);
        sel.quantity = qty;
        totalQty += qty;
        const price = sel.variant.price;
        originalTotal += price * qty;
        discountedTotal += this.getDiscountedPrice(price) * qty;
      });

      const meetsMinimum = totalQty >= this.minQty;

      return {
        original: originalTotal,
        discounted: meetsMinimum ? discountedTotal : originalTotal,
        savings: meetsMinimum ? originalTotal - discountedTotal : 0,
        count: selectedItems.length,
        totalQty,
        meetsMinimum,
      };
    }

    render() {
      const totals = this.calculateTotals();

      let html = `<div class="bw-wrapper">`;
      html += `<div class="bw-header">`;
      html += `<div class="bw-header__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="13" rx="1"/><path d="M12 8v13"/><path d="M3 12h18"/><path d="M19 8c0-1.5-2-4-4-4-1.5 0-2.5 1-3 2-.5-1-1.5-2-3-2-2 0-4 2.5-4 4"/></svg></div>`;
      html += `<div class="bw-header__text"><h3 class="bw-header__title">${this.escapeHtml(this.heading)}</h3>`;
      html += `<p class="bw-header__subtitle">Curated for you \u2014 better together</p></div>`;
      if (this.discountPct > 0) {
        html += `<span class="bw-header__discount-tag">${this.discountPct}% Off</span>`;
      }
      html += `</div>`;

      html += `<ul class="bw-products">`;
      const isCurrentProduct = (id) => id === this.currentProductId;
      this.products.forEach((prod, idx) => {
        const sel = this.selections[prod.id];
        const variant = sel.variant;
        const isChecked = sel.checked;
        const isCurrent = isCurrentProduct(prod.id);
        const hasMultipleVariants = prod.variants.length > 1 && !(prod.variants.length === 1 && prod.variants[0].title === 'Default Title');

        html += `<li class="bw-product${isChecked ? '' : ' is-unchecked'}${isCurrent ? ' is-current' : ''}" data-product-id="${prod.id}">`;

        html += `<div class="bw-product__check" data-product-id="${prod.id}" role="checkbox" aria-checked="${isChecked}" tabindex="0" aria-label="Include ${this.escapeHtml(prod.title)} in bundle"><span class="bw-check__box${isChecked ? ' is-checked' : ''}"><svg viewBox="0 0 12 10" fill="none"><path d="M1 5.5L4 8.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></div>`;

        const imgSrc = prod.featured_image ? this.getSizedImageUrl(prod.featured_image, '160x160') : '';
        html += `<div class="bw-product__image">`;
        if (imgSrc) {
          html += `<img src="${imgSrc}" alt="${this.escapeHtml(prod.title)}" loading="lazy" width="70" height="70">`;
        }
        html += `</div>`;

        html += `<div class="bw-product__info">`;
        html += `<div class="bw-product__details">`;
        if (isCurrent) {
          html += `<span class="bw-product__badge">This item</span>`;
        }
        const q = this.clampQty(sel.quantity || 1);
        html += `<p class="bw-product__title"><a href="${prod.url}">${this.escapeHtml(prod.title)}</a>${q > 1 ? '<span class="bw-product__qty-badge" data-qty-badge="' + prod.id + '">\u00a0\u00d7\u00a0' + q + '</span>' : '<span class="bw-product__qty-badge" data-qty-badge="' + prod.id + '"></span>'}</p>`;

        if (hasMultipleVariants) {
          html += `<div class="bw-product__variant"><select data-product-id="${prod.id}" aria-label="Select variant for ${this.escapeHtml(prod.title)}">`;
          prod.variants.forEach((v) => {
            const disabled = !v.available ? ' disabled' : '';
            const selected = v.id === variant.id ? ' selected' : '';
            const soldOutText = !v.available ? ' - Sold out' : '';
            html += `<option value="${v.id}"${selected}${disabled}>${this.escapeHtml(v.title)}${soldOutText}</option>`;
          });
          html += `</select></div>`;
        }

        html += `<div class="bw-product__prices">${this.getPricesHtmlForProduct(prod, totals.meetsMinimum)}</div>`;
        html += `</div>`;

        const qtyHidden = !variant.available;
        const qtyDisabled = !variant.available;
        html += `<div class="bw-product__qty"${qtyHidden ? ' hidden' : ''} data-product-id="${prod.id}">`;
        html += `<div class="bw-qty bw-qty--cart" role="group" aria-label="Quantity for ${this.escapeHtml(prod.title)}">`;
        html += `<button type="button" class="bw-qty__btn" data-product-id="${prod.id}" data-qty-delta="-1" aria-label="Decrease quantity"${qtyDisabled ? ' disabled' : ''}>\u2212</button>`;
        html += `<input type="number" class="bw-qty__input" min="1" max="${this.maxQty}" value="${q}" data-product-id="${prod.id}" inputmode="numeric" aria-label="Quantity"${qtyDisabled ? ' disabled' : ''} />`;
        html += `<button type="button" class="bw-qty__btn" data-product-id="${prod.id}" data-qty-delta="1" aria-label="Increase quantity"${qtyDisabled ? ' disabled' : ''}>+</button>`;
        html += `</div></div>`;

        html += `</div>`;
        html += `</li>`;
      });
      html += `</ul>`;
      html += `<div class="bw-divider"></div>`;

      html += `<div class="bw-bottom">`;

      const minNeeded = this.minQty - totals.totalQty;

      html += `<div class="bw-savings"${totals.meetsMinimum && totals.savings > 0 ? '' : ' hidden'}>`;
      html += `<svg class="bw-savings__icon-inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>`;
      html += `<span class="bw-savings__text">You save <span class="bw-savings__amount">${this.formatMoney(totals.savings)}</span></span>`;
      html += `</div>`;

      html += `<div class="bw-min-info"${!totals.meetsMinimum && totals.count > 0 ? '' : ' hidden'}>`;
      html += `<svg class="bw-min-info__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;
      html += `<span class="bw-min-info__text">Add <strong>${minNeeded > 0 ? minNeeded : 0}</strong> more item${minNeeded !== 1 ? 's' : ''} to unlock <strong>${this.discountPct}% off</strong> bundle discount</span>`;
      html += `</div>`;

      html += `<div class="bw-price-summary"${totals.count === 0 ? ' hidden' : ''}>`;
      html += `<div class="bw-price-summary__row bw-price-summary__row--orig"${!totals.meetsMinimum ? ' hidden' : ''}><span class="bw-price-summary__label">Total value</span><span class="bw-price-summary__value bw-price-summary__original">${this.formatMoney(totals.original)}</span></div>`;
      html += `<div class="bw-price-summary__row"><span class="bw-price-summary__label">${totals.meetsMinimum ? 'Bundle price' : 'Total'}</span><span class="bw-price-summary__value bw-price-summary__final">${this.formatMoney(totals.discounted)}</span></div>`;
      html += `</div>`;

      html += `<div class="bw-add-btn"><button type="button" class="btn btn--primary"${totals.count === 0 ? ' disabled' : ''}>`;
      html += `<span class="bw-add-btn__text">${totals.count > 0 ? 'Add Bundle To Cart \u2014 ' + this.formatMoney(totals.discounted) : 'Add Bundle to Cart'}</span>`;
      html += `<span class="bw-add-btn__spinner"></span>`;
      html += `</button></div>`;

      html += `<div class="bw-trust">`;
      html += `<span class="bw-trust__item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg> Secure Checkout</span>`;
      html += `<span class="bw-trust__item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> Satisfaction Guaranteed</span>`;
      html += `</div>`;

      html += `</div>`;

      html += `<div class="bw-error" hidden></div>`;
      html += `</div>`;

      this.innerHTML = html;
      this.removeAttribute('hidden');
      this.bindEvents();
    }

    bindEvents() {
      this.querySelectorAll('.bw-product__check[data-product-id]').forEach((el) => {
        const toggle = () => {
          const prodId = parseInt(el.dataset.productId, 10);
          if (this.selections[prodId]) {
            this.selections[prodId].checked = !this.selections[prodId].checked;
          }
          this.updateUI();
        };
        el.addEventListener('click', toggle);
        el.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            toggle();
          }
        });
      });

      this.querySelectorAll('.bw-product__variant select').forEach((sel) => {
        sel.addEventListener('change', (e) => {
          const prodId = parseInt(e.target.dataset.productId, 10);
          const variantId = parseInt(e.target.value, 10);
          const prod = this.products.find((p) => p.id === prodId);
          if (prod && this.selections[prodId]) {
            const variant = prod.variants.find((v) => v.id === variantId);
            if (variant) {
              this.selections[prodId].variantId = variant.id;
              this.selections[prodId].variant = variant;
            }
          }
          this.updateUI();
        });
      });

      this.querySelectorAll('.bw-qty__btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const prodId = parseInt(btn.dataset.productId, 10);
          const delta = parseInt(btn.dataset.qtyDelta, 10);
          const row = this.selections[prodId];
          if (!row || !row.checked || !row.variant.available) return;
          row.quantity = this.clampQty((row.quantity || 1) + delta);
          const inp = this.querySelector(`.bw-qty__input[data-product-id="${prodId}"]`);
          if (inp) inp.value = row.quantity;
          this.updateUI();
        });
      });

      this.querySelectorAll('.bw-qty__input').forEach((inp) => {
        inp.addEventListener('change', () => this.onQtyInputCommit(inp));
        inp.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            inp.blur();
          }
        });
      });

      const addBtn = this.querySelector('.bw-add-btn button');
      if (addBtn) {
        addBtn.addEventListener('click', () => this.addBundleToCart());
      }
    }

    onQtyInputCommit(inp) {
      const prodId = parseInt(inp.dataset.productId, 10);
      if (!this.selections[prodId]) return;
      const v = this.clampQty(inp.value);
      inp.value = v;
      this.selections[prodId].quantity = v;
      this.updateUI();
    }

    updateUI() {
      const totals = this.calculateTotals();

      this.products.forEach((prod) => {
        const sel = this.selections[prod.id];
        const li = this.querySelector(`li.bw-product[data-product-id="${prod.id}"]`);
        if (!li) return;

        li.classList.toggle('is-unchecked', !sel.checked);

        const checkBtn = li.querySelector('.bw-product__check');
        if (checkBtn) {
          checkBtn.setAttribute('aria-checked', String(sel.checked));
          const box = checkBtn.querySelector('.bw-check__box');
          if (box) box.classList.toggle('is-checked', sel.checked);
        }

        const qtyBadge = li.querySelector(`[data-qty-badge="${prod.id}"]`);
        if (qtyBadge) {
          const currentQty = this.clampQty(sel.quantity || 1);
          qtyBadge.textContent = currentQty > 1 ? '\u00a0\u00d7\u00a0' + currentQty : '';
        }

        const pricesEl = li.querySelector('.bw-product__prices');
        if (pricesEl) {
          pricesEl.innerHTML = this.getPricesHtmlForProduct(prod, totals.meetsMinimum);
        }

        const qtyWrap = li.querySelector('.bw-product__qty');
        if (qtyWrap) {
          qtyWrap.hidden = !sel.variant.available;
          const inp = qtyWrap.querySelector('.bw-qty__input');
          if (inp) {
            inp.max = String(this.maxQty);
            inp.value = this.clampQty(sel.quantity || 1);
            sel.quantity = this.clampQty(inp.value);
          }
          const disableQty = !sel.variant.available;
          qtyWrap.querySelectorAll('.bw-qty__btn, .bw-qty__input').forEach((el) => {
            el.disabled = disableQty;
          });
        }
      });

      const savingsEl = this.querySelector('.bw-savings');
      if (savingsEl) {
        if (totals.meetsMinimum && totals.savings > 0 && totals.count > 0) {
          savingsEl.hidden = false;
          const amountEl = savingsEl.querySelector('.bw-savings__amount');
          if (amountEl) amountEl.textContent = this.formatMoney(totals.savings);
        } else {
          savingsEl.hidden = true;
        }
      }

      const minInfoEl = this.querySelector('.bw-min-info');
      if (minInfoEl) {
        if (!totals.meetsMinimum && totals.count > 0) {
          minInfoEl.hidden = false;
          const minNeeded = Math.max(0, this.minQty - totals.totalQty);
          const textEl = minInfoEl.querySelector('.bw-min-info__text');
          if (textEl) {
            textEl.innerHTML = `Add <strong>${minNeeded}</strong> more item${minNeeded !== 1 ? 's' : ''} to unlock <strong>${this.discountPct}% off</strong> bundle discount`;
          }
        } else {
          minInfoEl.hidden = true;
        }
      }

      const summaryEl = this.querySelector('.bw-price-summary');
      if (summaryEl) {
        summaryEl.hidden = totals.count === 0;
        const origRow = summaryEl.querySelector('.bw-price-summary__row--orig');
        const origEl = summaryEl.querySelector('.bw-price-summary__original');
        const finalEl = summaryEl.querySelector('.bw-price-summary__final');
        const finalLabel = summaryEl.querySelector('.bw-price-summary__row:last-child .bw-price-summary__label');
        if (totals.meetsMinimum && this.discountPct > 0) {
          if (origRow) origRow.hidden = false;
          if (origEl) origEl.textContent = this.formatMoney(totals.original);
          if (finalEl) finalEl.textContent = this.formatMoney(totals.discounted);
          if (finalLabel) finalLabel.textContent = 'Bundle price';
        } else {
          if (origRow) origRow.hidden = true;
          if (finalEl) finalEl.textContent = this.formatMoney(totals.original);
          if (finalLabel) finalLabel.textContent = 'Total';
        }
      }

      const addBtn = this.querySelector('.bw-add-btn button');
      if (addBtn) {
        addBtn.disabled = totals.count === 0;
        const textEl = addBtn.querySelector('.bw-add-btn__text');
        if (textEl) {
          textEl.textContent = totals.count > 0
            ? `Add Bundle \u2014 ${this.formatMoney(totals.discounted)}`
            : 'Add Bundle to Cart';
        }
      }
    }

    async applyDiscountCode() {
      if (!this.discountCode || this.discountApplied) return;
      this.discountApplied = true;
      try {
        await fetch(`/discount/${encodeURIComponent(this.discountCode)}`, {
          method: 'GET',
          credentials: 'same-origin',
        });
      } catch {
        this.discountApplied = false;
      }
    }

    async addBundleToCart() {
      const selectedItems = this.getSelectedItems();
      if (selectedItems.length === 0) return;

      const addBtn = this.querySelector('.bw-add-btn button');
      const errorEl = this.querySelector('.bw-error');
      if (errorEl) errorEl.hidden = true;

      if (addBtn) {
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;
      }

      const totals = this.calculateTotals();

      if (this.discountCode && totals.meetsMinimum) {
        await this.applyDiscountCode();
      }

      const items = selectedItems.map((prod) => {
        const sel = this.selections[prod.id];
        const qty = this.clampQty(sel.quantity || 1);
        sel.quantity = qty;
        return {
          id: sel.variantId,
          quantity: qty,
          properties: {
            _bundle: 'true',
            _bundle_discount: totals.meetsMinimum && this.discountPct > 0 ? this.discountPct + '%' : '0%',
          },
        };
      });

      const cartDrawer = document.querySelector('cart-drawer');
      const isCartPage = document.body.classList.contains('cart-template');
      const isDrawer = window.FoxTheme && FoxTheme.settings && FoxTheme.settings.cartType === 'drawer';

      if (isCartPage || !isDrawer) {
        FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, {
          parameters: { items },
        });
        return;
      }

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        section_url: window.location.pathname,
      });

      try {
        const response = await fetch(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body,
        });
        const parsed = await response.json();

        if (parsed.status) {
          if (errorEl) {
            errorEl.textContent = parsed.description || 'Could not add bundle to cart.';
            errorEl.hidden = false;
          }
          return;
        }

        const cartResp = await fetch(FoxTheme.routes.cart_url, {
          ...FoxTheme.utils.fetchConfig(),
        });
        const cartJson = await cartResp.json();
        cartJson['sections'] = parsed['sections'];

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', { detail: { product: parsed } })
        );

        if (cartDrawer) cartDrawer.show(addBtn);
      } catch (err) {
        console.error('BundleWidget: add to cart error', err);
        if (errorEl) {
          errorEl.textContent = 'Something went wrong. Please try again.';
          errorEl.hidden = false;
        }
      } finally {
        if (addBtn) {
          addBtn.classList.remove('is-loading');
          addBtn.disabled = false;
        }
      }
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    getSizedImageUrl(src, size) {
      if (!src) return '';
      const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
      if (match) {
        const prefix = src.split(match[0]);
        return prefix[0] + '_' + size + '.' + match[1] + (match[2] || '');
      }
      return src;
    }
  }

  customElements.define('bundle-widget', BundleWidget);
}
